import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft,
  ArrowRight,
  UserPlus,
  CreditCard,
  QrCode,
  Share2,
  Smartphone,
  BarChart3,
  Printer,
  Package,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';

const HowToGuidePage = () => {
  const navigate = useNavigate();
  const [expandedStep, setExpandedStep] = useState(null);

  const steps = [
    {
      id: 1,
      icon: UserPlus,
      title: 'Create Your Account',
      description: 'Sign up for free to get started with your digital business card.',
      details: [
        'Click "Get Started" on the landing page',
        'Enter your email address and create a password',
        'Verify your email (if required)',
        'You\'re now ready to create your first card!'
      ],
      tip: 'Use a professional email address that you check regularly.'
    },
    {
      id: 2,
      icon: CreditCard,
      title: 'Create Your Digital Card',
      description: 'Fill in your professional information to create your digital business card.',
      details: [
        'Click "Create Card" from the dashboard',
        'Add your name, job title, and company',
        'Enter contact details: phone numbers, emails, websites',
        'Add social media links (LinkedIn, Twitter, etc.)',
        'Upload your profile photo for a personal touch',
        'Upload your company logo for branding'
      ],
      tip: 'You can add multiple phone numbers, emails, and addresses to a single card.'
    },
    {
      id: 3,
      icon: QrCode,
      title: 'Customize Your QR Code',
      description: 'Personalize your QR code with custom colors and your logo.',
      details: [
        'Go to your card\'s edit page',
        'Navigate to the "QR Code" tab',
        'Choose foreground and background colors',
        'Toggle "Include Profile Photo" to embed your photo in the QR code',
        'Preview the QR code in real-time',
        'Download as PNG for printing or sharing'
      ],
      tip: 'Keep good contrast between foreground and background colors for better scanning.'
    },
    {
      id: 4,
      icon: Share2,
      title: 'Share Your Card',
      description: 'Multiple ways to share your digital business card with others.',
      details: [
        'Direct Link: Copy and share the public URL of your card',
        'QR Code: Show or print your QR code for others to scan',
        'NFC: Program an NFC card with your profile URL',
        'Native Share: Use your device\'s share feature',
        'vCard Download: Recipients can save your contact directly'
      ],
      tip: 'The QR code works with any smartphone camera - no special app needed!'
    },
    {
      id: 5,
      icon: Smartphone,
      title: 'Set Up NFC Card',
      description: 'Program a physical NFC card to share your contact with a tap.',
      details: [
        'Purchase an NFC card (NTAG213, NTAG215, or NTAG216 recommended)',
        'Download an NFC writing app on your phone',
        'Go to your card\'s "NFC" tab and copy the URL',
        'Open the NFC writer app and select "Write URL"',
        'Paste your card URL and tap the NFC card to program it',
        'Test by tapping the card with any NFC-enabled phone'
      ],
      tip: 'NTAG215 cards are the most common and work with most smartphones.'
    },
    {
      id: 6,
      icon: BarChart3,
      title: 'Track Your Analytics',
      description: 'Monitor who views your card and how they interact with it.',
      details: [
        'Visit the Analytics page from the sidebar',
        'View total scans, daily/weekly/monthly stats',
        'See device breakdown (mobile vs desktop)',
        'Check the "Card Views" page for detailed history',
        'View location, browser, and device information for each scan',
        'Export your data as CSV for further analysis'
      ],
      tip: 'Check your analytics regularly to understand your networking reach.'
    },
    {
      id: 7,
      icon: Printer,
      title: 'Design Physical Cards',
      description: 'Create print-ready designs for your physical business cards.',
      details: [
        'Open your card and click "Print Preview"',
        'Choose between horizontal or vertical layouts',
        'Select a design template (Professional, Modern, Minimal)',
        'Pick a color scheme that matches your brand',
        'Toggle which fields to include on the card',
        'Download the design as a high-quality image',
        'Send to your preferred print shop'
      ],
      tip: 'Include a QR code on your physical card for a hybrid digital-physical experience.'
    },
    {
      id: 8,
      icon: Package,
      title: 'Order Physical Cards',
      description: 'Order professionally printed NFC-enabled business cards.',
      details: [
        'Click "Order Physical Card" from your card page',
        'Select your preferred design template and colors',
        'Choose whether to include a QR code',
        'Enter the quantity you need',
        'Fill in your shipping address',
        'Review and submit your order',
        'Our team will process and ship your cards'
      ],
      tip: 'Orders typically ship within 5-7 business days.'
    }
  ];

  const faqs = [
    {
      question: 'How many cards can I create?',
      answer: 'You can create unlimited digital business cards with your free account. Create different cards for different purposes - work, personal, freelance, etc.'
    },
    {
      question: 'Is my information secure?',
      answer: 'Yes! We use industry-standard encryption and security practices. Your data is stored securely and never shared with third parties.'
    },
    {
      question: 'Can I update my card after sharing?',
      answer: 'Absolutely! Any changes you make to your card are instantly reflected. The QR code and link remain the same, but the content updates automatically.'
    },
    {
      question: 'Do people need an app to view my card?',
      answer: 'No! Your card opens in any web browser. Anyone can scan your QR code with their phone camera - no special app required.'
    },
    {
      question: 'What NFC cards work with Identra?',
      answer: 'Any NFC card that supports URL writing will work. We recommend NTAG213, NTAG215, or NTAG216 cards, which are widely available online.'
    },
    {
      question: 'Can I use Identra on mobile?',
      answer: 'Yes! Identra is fully responsive and works great on smartphones and tablets. Create, edit, and share your cards from any device.'
    }
  ];

  const toggleStep = (stepId) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">How to Use Identra</h1>
            <p className="text-muted-foreground">Your complete guide to digital business cards</p>
          </div>
        </div>

        {/* Quick Start */}
        <Card className="bg-gradient-to-br from-[#16335E] to-[#0A1A34] text-white">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <Badge className="bg-white/20 text-white hover:bg-white/30 mb-3">Quick Start</Badge>
                <h2 className="text-xl font-bold mb-2">Ready to create your first card?</h2>
                <p className="text-white/80 mb-4">
                  It takes less than 2 minutes to create your professional digital business card.
                </p>
                <Button 
                  onClick={() => navigate('/cards/new')}
                  className="bg-white text-[#16335E] hover:bg-white/90"
                  data-testid="create-card-btn"
                >
                  Create Your Card
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className="hidden md:flex items-center justify-center w-32 h-32 bg-white/10 rounded-2xl">
                <CreditCard className="w-16 h-16 text-white/80" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step by Step Guide */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Step-by-Step Guide</h2>
          
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isExpanded = expandedStep === step.id;
            
            return (
              <Card 
                key={step.id}
                className={`transition-all duration-200 cursor-pointer ${isExpanded ? 'ring-2 ring-[#16335E]/20' : ''}`}
                onClick={() => toggleStep(step.id)}
                data-testid={`step-${step.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#16335E]/10 text-[#16335E] flex-shrink-0">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-xs">Step {step.id}</Badge>
                          <h3 className="font-semibold">{step.title}</h3>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <p className="text-muted-foreground mt-1">{step.description}</p>
                      
                      {isExpanded && (
                        <div className="mt-4 space-y-4">
                          <ul className="space-y-2">
                            {step.details.map((detail, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-[#00A78B] flex-shrink-0 mt-0.5" />
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                          
                          <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-sm">
                              <span className="font-medium text-[#16335E]">Pro Tip:</span>{' '}
                              {step.tip}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQs */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Frequently Asked Questions</h2>
          
          <div className="grid gap-4">
            {faqs.map((faq, index) => (
              <Card key={index} data-testid={`faq-${index}`}>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground text-sm">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Help CTA */}
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="font-semibold mb-2">Still have questions?</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Contact our support team for personalized assistance.
              </p>
              <Button 
                variant="outline"
                onClick={() => window.open('mailto:support@identra.co', '_blank')}
                data-testid="contact-support-btn"
              >
                Contact Support
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default HowToGuidePage;
